<!-- src/layouts/MainLayout.vue -->
<template>
  <div v-if="hasProfileFallback" class="layout" :class="{ 'sidebar-collapsed': sidebarCollapsed, 'mobile-menu-open': mobileMenuOpen }">
    <!-- Mobile Header -->
    <header class="mobile-header">
      <button class="mobile-menu-btn" @click="toggleMobileMenu" aria-label="Menu">
        <i :class="mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'"></i>
      </button>
      <div class="mobile-brand">
        <span class="brand-icon">🏥</span>
        <span class="brand-name">Saúde Digital</span>
      </div>
    </header>

    <!-- Overlay for mobile -->
    <div 
      class="mobile-overlay" 
      :class="{ active: mobileMenuOpen }"
      @click="closeMobileMenu"
    ></div>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'mobile-open': mobileMenuOpen }">
      <div class="logo-wrapper">
        <div class="brand">
          <span class="brand-icon">🏥</span>
          <div class="brand-text">
            <span class="brand-name">Saúde Digital</span>
            <span class="brand-tagline">Portal Admin</span>
          </div>
        </div>
        <button
          class="collapse-btn hide-mobile"
          @click="toggleSidebar"
          title="Ocultar menu"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <button
          class="close-mobile-btn hide-desktop"
          @click="closeMobileMenu"
          title="Fechar menu"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <nav class="menu">
        <template v-for="route in visibleRoutes" :key="route.path">
          <!-- Item simples (sem children) -->
          <router-link
            v-if="!route.children || route.children.length === 0"
            :to="route.path"
            @click="closeMobileMenu"
          >
            <i :class="route.icon"></i>
            <span class="menu-label">{{ route.label }}</span>
          </router-link>

          <!-- Grupo expansível (PRD-002 RF-17) -->
          <div v-else class="menu-group" :class="{ expanded: isGroupExpanded(route.path) }">
            <button
              type="button"
              class="menu-group-toggle"
              :class="{ active: isChildRouteActive(route) }"
              @click="toggleGroup(route.path)"
            >
              <i :class="route.icon"></i>
              <span class="menu-label">{{ route.label }}</span>
              <i class="fas fa-chevron-down chevron" />
            </button>
            <div v-show="isGroupExpanded(route.path)" class="menu-children">
              <router-link
                v-for="child in visibleChildren(route)"
                :key="child.path"
                :to="joinPath(route.path, child.path)"
                @click="closeMobileMenu"
              >
                <i :class="child.icon"></i>
                <span class="menu-label">{{ child.menuLabel || child.label }}</span>
                <span
                  v-if="child.path === 'action-center' && pendingActionsCount > 0"
                  class="menu-badge"
                >{{ pendingActionsCount > 99 ? '99+' : pendingActionsCount }}</span>
              </router-link>
            </div>
          </div>
        </template>
      </nav>

      <!-- Menu de Sistema (Configurações) - sempre no final -->
      <nav class="menu menu-system" v-if="settingsRoute">
        <router-link
          :to="settingsRoute.path"
          @click="closeMobileMenu"
        >
          <i :class="settingsRoute.icon"></i> 
          <span class="menu-label">{{ settingsRoute.label }}</span>
        </router-link>
      </nav>

      <div class="user-info">
        <router-link to="/me" class="user-profile-link" :title="fullName + ' - ' + profileLabel">
          <div class="user-avatar-wrapper">
            <div class="user-avatar" :style="{ '--level-color': adminLevelBadge.color }">
              <img
                v-if="avatarUrl && !avatarError"
                :src="avatarUrl"
                :alt="fullName"
                class="avatar-img"
                @error="onAvatarError"
              />
              <span v-else>{{ initials }}</span>
            </div>
            <span class="level-indicator" :style="{ background: adminLevelBadge.color }">
              <i :class="adminLevelBadge.icon"></i>
            </span>
          </div>
          <div class="user-details">
            <p class="name">{{ displayName }}</p>
            <p class="role" :style="{ color: adminLevelBadge.color }">{{ profileLabel }}</p>
          </div>
        </router-link>
        <button @click="logout" class="logout-btn">
          <i class="fas fa-sign-out-alt"></i> Sair
        </button>
        <div class="version-info">v{{ appVersion }}</div>
      </div>
    </aside>

    <!-- Expand Button (só aparece quando menu está oculto no desktop) -->
    <button
      v-if="sidebarCollapsed && !isMobile"
      class="expand-btn"
      @click="toggleSidebar"
      title="Mostrar menu"
    >
      <i class="fas fa-bars"></i>
    </button>

    <div class="main">
      <router-view />

      <!-- Convite de instalação do PWA (Android prompt / guia iOS) — RF-3 / PRD-022.
           DENTRO do `.main` (BUG-051): `.layout` é `display: flex` em LINHA, então
           um banner pendurado como filho dele não empilha embaixo do conteúdo —
           vira uma coluna espremida ao lado. Nenhuma media query corrigia, porque
           `.layout` nunca vira `column`. -->
      <InstallInvite v-if="pwaInstallEnabled" />
    </div>

    <!-- Convite de ativação de push — PRD-022 INC-2 / BUG-051.
         Fica fora do `.main` por ser modal: BaseModal teleporta para o `body`,
         então não participa de nenhum fluxo de layout. -->
    <PushEnableModal v-if="pushEnabled" />

    <!-- Aviso de nova versão (BUG-051). Também teleporta para o `body`. -->
    <AppUpdateBanner />
  </div>

  <!-- Tela de erro ao carregar perfil -->
  <div v-else-if="profileLoadError" class="error-layout">
    <div class="error-card">
      <!-- Header com gradiente -->
      <div class="error-card-header">
        <div class="error-icon-wrapper">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h2>Ops! Algo deu errado</h2>
        <p class="error-subtitle">{{ profileLoadError }}</p>
      </div>
      
      <!-- Corpo do card -->
      <div class="error-card-body">
        <!-- Ações principais -->
        <div class="error-actions">
          <button @click="retryLoadProfile" class="btn-primary" :disabled="isRetrying">
            <i :class="isRetrying ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'"></i>
            {{ isRetrying ? 'Tentando...' : 'Tentar novamente' }}
          </button>
          <button @click="handleLogout" class="btn-secondary">
            <i class="fas fa-sign-out-alt"></i>
            Fazer login novamente
          </button>
        </div>
        
        <!-- Separador -->
        <div class="error-divider">
          <span>Informações para suporte</span>
        </div>
        
        <!-- Card de informações do usuário -->
        <div class="user-info-card">
          <div class="user-info-header">
            <div class="user-avatar">
              <span>{{ userContextInfo.initials }}</span>
            </div>
            <div class="user-details">
              <h4>{{ userContextInfo.name || 'Usuário' }}</h4>
              <p>{{ userContextInfo.email || 'E-mail não disponível' }}</p>
            </div>
            <span class="user-level-badge">{{ userContextInfo.levelName || 'Admin' }}</span>
          </div>
          <div class="user-info-grid">
            <div class="info-item">
              <span class="info-label">Profile ID</span>
              <span class="info-value code">{{ userContextInfo.profileId || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">ID do Erro</span>
              <span class="info-value code error-id">{{ errorId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Data/Hora</span>
              <span class="info-value">{{ errorTimestamp }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Navegador</span>
              <span class="info-value">{{ systemInfo.browser }}</span>
            </div>
          </div>
        </div>
        
        <!-- Botão de Copiar Relatório -->
        <button @click="copyTicketReport" class="btn-copy-report" :class="{ copied: copiedSuccess }">
          <i :class="copiedSuccess ? 'fas fa-check-circle' : 'fas fa-clipboard-list'"></i>
          <span>{{ copiedSuccess ? 'Copiado!' : 'Copiar Relatório para Chamado' }}</span>
        </button>
        
        <!-- Detalhes técnicos - aberto por padrão -->
        <details class="technical-details" open>
          <summary>
            <i class="fas fa-terminal"></i>
            <span>Relatório técnico completo</span>
            <i class="fas fa-chevron-down chevron"></i>
          </summary>
          <div class="technical-content">
            <pre>{{ ticketReportText }}</pre>
          </div>
        </details>
      </div>
    </div>
  </div>

  <!-- Tela de carregamento enquanto o profile ainda não existe -->
  <div v-else class="loading-layout">
    <div class="loading-spinner"></div>
    <p>Carregando perfil...</p>
    <p v-if="loadingTooLong" class="loading-hint">
      Isso está demorando mais que o esperado...
      <button @click="retryLoadProfile" class="btn-text">Tentar novamente</button>
    </p>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { appRoutes } from '@/router/routes';
import { ADMIN_LEVEL_NAMES, ADMIN_LEVEL_BADGES } from '@/constants/adminLevels';
import { hasMinimumLevel } from '@/constants/adminLevels';
import { useSecurityRefresh } from '@/composables/useSecurityRefresh';
import { logger } from '@/utils/logger';
import { FEATURE_FLAGS } from '@/config/featureFlags';
import InstallInvite from '@/components/pwa/InstallInvite.vue';
import PushEnableModal from '@/components/push/PushEnableModal.vue';
import AppUpdateBanner from '@/components/app/AppUpdateBanner.vue';

const currentRoute = useRoute();

// PRD-022 RF-3 — convite de instalação do PWA, atrás do kill switch de rollout.
const pwaInstallEnabled = computed(() => FEATURE_FLAGS.pwa?.installEnabled === true);
const pushEnabled = computed(() => FEATURE_FLAGS.pwa?.pushEnabled === true);

// PRD-002 RF-17 — estado de expand/collapse dos grupos hierárquicos.
// Persiste em localStorage pra que o operador não tenha que re-expandir
// a cada login. Default: expandido se rota ativa for filha.
const MENU_EXPANDED_STORAGE_KEY = 'sda_menu_expanded';
const expandedGroups = ref(loadExpandedGroups());

function loadExpandedGroups() {
  try {
    const raw = localStorage.getItem(MENU_EXPANDED_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function persistExpandedGroups() {
  try {
    localStorage.setItem(
      MENU_EXPANDED_STORAGE_KEY,
      JSON.stringify(Array.from(expandedGroups.value))
    );
  } catch {
    // localStorage indisponível (private mode etc) — ignora
  }
}

function isGroupExpanded(groupPath) {
  return expandedGroups.value.has(groupPath);
}

function toggleGroup(groupPath) {
  if (expandedGroups.value.has(groupPath)) {
    expandedGroups.value.delete(groupPath);
  } else {
    expandedGroups.value.add(groupPath);
  }
  // Reactivity: replace Set pra trigger re-render
  expandedGroups.value = new Set(expandedGroups.value);
  persistExpandedGroups();
}

function isChildRouteActive(group) {
  if (!group.children) return false;
  return group.children.some((child) =>
    currentRoute.path.startsWith(joinPath(group.path, child.path))
  );
}

function joinPath(parent, child) {
  // Filho com path absoluto (ex.: grupo só-de-menu Operação/Gestão/Agenda) usa o
  // próprio path — não herda o do pai. Filho relativo (regula-hub) aninha sob o pai.
  if (child.startsWith('/')) return child.replace(/\/+/g, '/');
  return (parent.replace(/\/$/, '') + '/' + child.replace(/^\//, '')).replace(/\/+/g, '/');
}

function visibleChildren(group) {
  return (group.children || []).filter(
    (child) =>
      child.label && // filhos sem label (ex.: redirects recoverable/alerts) não vão pro menu
      hasMinimumLevel(auth.adminLevelValue, child.minLevel || 0)
  );
}

const auth = useAuthStore();
const { fullName, hasProfile } = storeToRefs(auth);

// Fallback: verifica localStorage se o store ainda não sincronizou
const hasProfileFallback = computed(() => {
  if (hasProfile.value) return true;
  
  // Verifica localStorage como fallback
  const storedProfile = localStorage.getItem('profile');
  const storedToken = localStorage.getItem('token');
  
  if (storedToken && storedProfile && storedProfile !== 'null') {
    try {
      const profile = JSON.parse(storedProfile);
      if (profile && Object.keys(profile).length > 0) {
        // Sincroniza o store com o localStorage
        logger.debug('MainLayout: Sincronizando store com localStorage');
        const adminLevel = parseInt(localStorage.getItem('admin_level')) || 0;
        const adminLevelName = localStorage.getItem('admin_level_name') || '';
        auth.updateProfile({ profile, adminLevel, adminLevelName });
        return true;
      }
    } catch (e) {
      logger.warn('MainLayout: Erro ao parsear profile do localStorage', e);
    }
  }
  
  return false;
});

// Estado de erro de carregamento do perfil
const profileLoadError = ref(null);
const isRetrying = ref(false);
const loadingTooLong = ref(false);
const errorTimestamp = ref('');
const errorId = ref('');
const copiedSuccess = ref(false);
const currentUrl = computed(() => window.location.href);
const loadingTimeoutRef = ref(null);

// Informações do contexto do usuário para exibição na tela de erro
const userContextInfo = computed(() => {
  const profile = auth.profile;
  
  // Tenta buscar do profile primeiro, depois do localStorage
  let storedProfile = null;
  try {
    const stored = localStorage.getItem('profile');
    if (stored) storedProfile = JSON.parse(stored);
  } catch { /* ignore */ }
  
  const email = profile?.email || storedProfile?.email || localStorage.getItem('user_email') || '';
  const firstName = profile?.first_name || storedProfile?.first_name || '';
  const lastName = profile?.last_name || storedProfile?.last_name || '';
  const name = firstName || lastName ? `${firstName} ${lastName}`.trim() : '';
  const profileId = profile?.id || profile?.profile_id || storedProfile?.id || storedProfile?.profile_id || '';
  
  // Gera iniciais do nome
  let initials = '?';
  if (firstName && lastName) {
    initials = `${firstName[0]}${lastName[0]}`.toUpperCase();
  } else if (firstName) {
    initials = firstName.substring(0, 2).toUpperCase();
  } else if (email) {
    initials = email.substring(0, 2).toUpperCase();
  }
  
  // Admin level do store ou localStorage
  const levelValue = auth.adminLevelValue || parseInt(localStorage.getItem('admin_level')) || 0;
  const levelName = auth.adminLevelDisplayName || localStorage.getItem('admin_level_name') || ADMIN_LEVEL_NAMES[levelValue] || '';
  
  return {
    email,
    name,
    profileId,
    profileIdShort: profileId ? `${profileId.substring(0, 8)}...` : '',
    initials,
    levelName,
    level: levelValue
  };
});

// Informações do sistema para diagnóstico
const systemInfo = computed(() => {
  const ua = navigator.userAgent;
  
  // Detecta navegador
  let browser = 'Desconhecido';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  
  // Versão do navegador
  let browserVersion = '';
  const versionMatch = ua.match(/(Firefox|Chrome|Safari|Edg|OPR)\/(\d+(\.\d+)?)/);
  if (versionMatch) browserVersion = versionMatch[2];
  
  // Detecta SO
  let os = 'Desconhecido';
  if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  // Detecta dispositivo
  let device = 'Desktop';
  if (ua.includes('Mobile') || ua.includes('Android')) device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad')) device = 'Tablet';
  
  return {
    browser: `${browser} ${browserVersion}`.trim(),
    os,
    device,
    screenSize: `${window.screen.width}x${window.screen.height}`,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: `${window.screen.colorDepth} bits`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
    cookiesEnabled: navigator.cookieEnabled,
    platform: navigator.platform,
    memory: navigator.deviceMemory ? `${navigator.deviceMemory} GB` : 'N/A',
    cores: navigator.hardwareConcurrency || 'N/A',
    connection: navigator.connection ? navigator.connection.effectiveType : 'N/A'
  };
});

/**
 * Formata o user agent em múltiplas linhas para caber no quadro
 */
function formatUserAgent(ua) {
  const maxLen = 58; // Largura máxima do conteúdo dentro do quadro
  const lines = [];
  let remaining = ua;
  
  while (remaining.length > 0) {
    if (remaining.length <= maxLen) {
      lines.push(`│ ${remaining.padEnd(60)}`);
      break;
    }
    // Tenta quebrar em um espaço
    let breakPoint = remaining.lastIndexOf(' ', maxLen);
    if (breakPoint <= 0) breakPoint = maxLen;
    lines.push(`│ ${remaining.substring(0, breakPoint).padEnd(60)}`);
    remaining = remaining.substring(breakPoint).trim();
  }
  
  return lines;
}

// Relatório completo para chamado (formatado para ClickUp/Jira/etc)
const ticketReportText = computed(() => {
  const sys = systemInfo.value;
  const now = new Date();
  
  const lines = [
    '╔══════════════════════════════════════════════════════════════╗',
    '║         RELATÓRIO DE ERRO - SAÚDE DIGITAL ADMIN              ║',
    '╚══════════════════════════════════════════════════════════════╝',
    '',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    '',
    'OBSERVAÇÕES DO USUÁRIO:',
    '[Descreva aqui o que você estava tentando fazer quando o erro ocorreu]',
    '',
    '',
    '',
    'OBSERVAÇÕES DO RELATOR:',
    '[Se você não é o usuário afetado, descreva como obteve este relatório]',
    '',
    '',
    '',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    '',
    '',
    '╔══════════════════════════════════════════════════════════════╗',
    '║       DETALHES TÉCNICOS (POR FAVOR, NÃO ALTERAR)             ║',
    '╚══════════════════════════════════════════════════════════════╝',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ IDENTIFICAÇÃO DO ERRO                                       │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ ID do Erro:     ${errorId.value}`,
    `│ Data:           ${now.toLocaleDateString('pt-BR')}`,
    `│ Hora:           ${now.toLocaleTimeString('pt-BR')}`,
    `│ Timestamp:      ${now.toISOString()}`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ DADOS DO USUÁRIO                                            │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ Nome:           ${userContextInfo.value.name || 'N/A'}`,
    `│ E-mail:         ${userContextInfo.value.email || 'N/A'}`,
    `│ Profile ID:     ${userContextInfo.value.profileId || 'N/A'}`,
    `│ Nível Admin:    ${userContextInfo.value.levelName} (${userContextInfo.value.level})`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ DESCRIÇÃO DO ERRO                                           │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ Mensagem:       ${profileLoadError.value}`,
    `│ URL:            ${currentUrl.value}`,
    `│ Página:         ${document.title}`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ AMBIENTE DO USUÁRIO                                         │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ Navegador:      ${sys.browser}`,
    `│ Sistema Op.:    ${sys.os}`,
    `│ Dispositivo:    ${sys.device}`,
    `│ Plataforma:     ${sys.platform}`,
    `│ Idioma:         ${sys.language}`,
    `│ Fuso Horário:   ${sys.timezone}`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ INFORMAÇÕES TÉCNICAS                                        │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ Resolução Tela: ${sys.screenSize}`,
    `│ Janela:         ${sys.windowSize}`,
    `│ Profund. Cor:   ${sys.colorDepth}`,
    `│ Memória:        ${sys.memory}`,
    `│ CPU Cores:      ${sys.cores}`,
    `│ Conexão:        ${sys.connection}`,
    `│ Online:         ${sys.online ? 'Sim' : 'Não'}`,
    `│ Cookies:        ${sys.cookiesEnabled ? 'Habilitados' : 'Desabilitados'}`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ USER AGENT                                                  │',
    '├─────────────────────────────────────────────────────────────┤',
    ...formatUserAgent(navigator.userAgent),
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '┌─────────────────────────────────────────────────────────────┐',
    '│ STORAGE LOCAL (Debug)                                       │',
    '├─────────────────────────────────────────────────────────────┤',
    `│ Token:          ${localStorage.getItem('token') ? 'Presente' : 'Ausente'}`,
    `│ Refresh Token:  ${localStorage.getItem('refresh_token') ? 'Presente' : 'Ausente'}`,
    `│ Admin Level:    ${localStorage.getItem('admin_level') || 'N/A'}`,
    `│ Profile:        ${localStorage.getItem('profile') ? 'Presente' : 'Ausente'}`,
    '└─────────────────────────────────────────────────────────────┘',
    '',
    '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━',
    `Gerado em: ${now.toLocaleString('pt-BR')} | Saúde Digital Admin Portal`,
    ''
  ];
  return lines.join('\n');
});

// Inicializa o security refresh para sincronização periódica do perfil
const { refreshProfileNow, startPeriodicRefresh } = useSecurityRefresh();

const sidebarCollapsed = ref(false);
const mobileMenuOpen = ref(false);
const isMobile = ref(false);

// Check if mobile
function checkMobile() {
  isMobile.value = window.innerWidth < 768;
  if (!isMobile.value) {
    mobileMenuOpen.value = false;
  }
}

/**
 * Carrega o perfil com tratamento de erro
 */
async function loadProfileWithErrorHandling() {
  // Se já tem profile válido, não precisa carregar novamente
  if (hasProfileFallback.value) {
    logger.debug('MainLayout: Profile já existe, pulando carregamento');
    return;
  }
  
  profileLoadError.value = null;
  loadingTooLong.value = false;
  
  // Timer para mostrar mensagem se demorar muito
  loadingTimeoutRef.value = setTimeout(() => {
    if (!hasProfileFallback.value && !profileLoadError.value) {
      loadingTooLong.value = true;
    }
  }, 8000); // 8 segundos
  
  try {
    logger.debug('MainLayout: Carregando perfil...');
    const result = await refreshProfileNow(false);
    
    if (!result && !hasProfileFallback.value) {
      throw new Error('Não foi possível carregar o perfil do usuário');
    }
    
    logger.debug('MainLayout: Perfil carregado com sucesso');
  } catch (error) {
    logger.error('MainLayout: Erro ao carregar perfil', error);
    
    // Se não tem perfil no store, mostra erro
    if (!hasProfileFallback.value) {
      profileLoadError.value = getFriendlyErrorMessage(error);
      errorTimestamp.value = new Date().toLocaleString('pt-BR');
      errorId.value = generateErrorId();
    }
  } finally {
    if (loadingTimeoutRef.value) {
      clearTimeout(loadingTimeoutRef.value);
      loadingTimeoutRef.value = null;
    }
  }
}

/**
 * Tenta carregar o perfil novamente
 */
async function retryLoadProfile() {
  isRetrying.value = true;
  await loadProfileWithErrorHandling();
  isRetrying.value = false;
}

/**
 * Gera mensagem de erro amigável
 */
function getFriendlyErrorMessage(error) {
  const message = error?.message || '';
  
  if (message.includes('500') || message.includes('Internal Server')) {
    return 'O servidor encontrou um erro interno. Tente novamente em alguns instantes.';
  }
  if (message.includes('502') || message.includes('503') || message.includes('504')) {
    return 'O servidor está temporariamente indisponível. Tente novamente em alguns minutos.';
  }
  if (message.includes('401')) {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  if (message.includes('403')) {
    return 'Você não tem permissão para acessar este recurso.';
  }
  if (message.includes('network') || message.includes('fetch') || !navigator.onLine) {
    return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
  }
  
  return message || 'Ocorreu um erro ao carregar seu perfil. Tente novamente.';
}

/**
 * Gera ID único para o erro
 */
function generateErrorId() {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `ERR-${timestamp}-${random}`.toUpperCase();
}

/**
 * Copia o relatório completo para chamado
 */
async function copyTicketReport() {
  try {
    await navigator.clipboard.writeText(ticketReportText.value);
    copiedSuccess.value = true;
    setTimeout(() => { copiedSuccess.value = false; }, 3000);
  } catch {
    // Fallback para navegadores mais antigos
    const textarea = document.createElement('textarea');
    textarea.value = ticketReportText.value;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    copiedSuccess.value = true;
    setTimeout(() => { copiedSuccess.value = false; }, 3000);
  }
}

/**
 * Faz logout e redireciona para login
 */
function handleLogout() {
  auth.logout();
}

// D-006 / RF-34 (CA-4) — badge "ações pendentes" na Central de Ações.
// Mesmo padrão do badge de alertas; timer próprio (não reusar o de alertas).
const pendingActionsCount = ref(0);
let pendingActionsPollTimer = null;

async function pollPendingActions() {
  try {
    const { getPendingActionsCount } = await import('@/services/pendingActionsService');
    pendingActionsCount.value = await getPendingActionsCount();
  } catch { /* silent */ }
}

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
  autoExpandActiveGroup();
  pollPendingActions();
  pendingActionsPollTimer = setInterval(pollPendingActions, 60000);

  logger.debug('MainLayout: onMounted', {
    isLoggedIn: auth.isLoggedIn,
    hasProfile: hasProfile.value,
    hasProfileFallback: hasProfileFallback.value,
    profile: auth.profile,
    adminLevel: auth.adminLevelValue
  });
  
  // Inicia refresh periódico do perfil para capturar mudanças de permissão
  if (auth.isLoggedIn) {
    logger.debug('MainLayout: Iniciando security refresh');
    startPeriodicRefresh();
    
    // Só tenta carregar se não tem profile
    if (!hasProfileFallback.value) {
      loadProfileWithErrorHandling();
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
  if (loadingTimeoutRef.value) {
    clearTimeout(loadingTimeoutRef.value);
  }
  if (pendingActionsPollTimer) {
    clearInterval(pendingActionsPollTimer);
  }
});

function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value;
}

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

const visibleRoutes = computed(() =>
  appRoutes.filter((route) => {
    if (!route.label || route.path === '/settings') return false;
    // Grupo (tem children) só aparece se houver ao menos 1 filho visível pro
    // nível do usuário — evita grupo "vazio" quando todos os filhos são acima.
    if (route.children && route.children.length > 0) {
      return visibleChildren(route).length > 0;
    }
    return hasMinimumLevel(auth.adminLevelValue, route.minLevel || 0);
  })
);

// Auto-expande grupo cuja rota ativa é uma filha (PRD-002 RF-17). Roda no mount
// e quando route muda — UX: "se entrei direto via URL em /regula-hub/jobs, o
// grupo deve abrir sozinho na sidebar".
function autoExpandActiveGroup() {
  for (const route of appRoutes) {
    if (route.children && isChildRouteActive(route) && !expandedGroups.value.has(route.path)) {
      expandedGroups.value.add(route.path);
      expandedGroups.value = new Set(expandedGroups.value);
    }
  }
}

// Rota de configurações separada (fica no final do menu)
const settingsRoute = computed(() =>
  appRoutes.find(
    route => route.path === '/settings' && 
             hasMinimumLevel(auth.adminLevelValue, route.minLevel || 0)
  )
);

const profileLabel = computed(() =>
  ADMIN_LEVEL_NAMES[auth.adminLevelValue] || auth.adminLevelDisplayName || 'Administrador'
);

// Badge do admin level com cor e ícone
const adminLevelBadge = computed(() => 
  ADMIN_LEVEL_BADGES[auth.adminLevelValue] || { color: '#17a2b8', icon: 'fas fa-user' }
);

// Iniciais do usuário para avatar
const initials = computed(() => {
  if (!fullName.value) return '';
  const parts = fullName.value.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
});

// Nome formatado para exibição - apenas primeiro nome (padrão Slack/Discord)
const displayName = computed(() => {
  if (!fullName.value) return '';
  const parts = fullName.value.split(' ').filter(p => p.length > 0);
  return parts[0];
});

// URL do avatar do usuário
const avatarUrl = computed(() => auth.profile?.photo_url || null);

// A photo_url é uma URL assinada do GCS (X-Goog-Signature/Expires) que vive no
// localStorage. Ao reabrir o admin com a assinatura expirada, o <img> dá 403 e,
// sem tratamento, exibia o ícone de imagem quebrada (só voltava ao deslogar/logar,
// que reemite a URL). Aqui: no erro, caímos nas iniciais (nunca ícone quebrado) e
// buscamos um perfil fresco UMA vez — se o backend devolver uma photo_url nova, o
// watch reseta o erro e o <img> tenta de novo (auto-cura, sem re-login).
const avatarError = ref(false);
let avatarRefreshTried = false;

watch(avatarUrl, () => {
  avatarError.value = false;
});

async function onAvatarError() {
  avatarError.value = true;
  if (avatarRefreshTried) return;
  avatarRefreshTried = true;
  await refreshProfileNow(false);
}

// Versão do sistema — injetada no build (git SHA via VUE_APP_VERSION).
// Fallback para dev local, onde a env var não está definida.
const appVersion = process.env.VUE_APP_VERSION || 'dev';

function logout() {
  auth.logout();
}
</script>

<style scoped>
/* === LAYOUT BASE === */
.layout {
  display: flex;
  height: 100vh;
  font-family: 'Inter', sans-serif;
  background-color: var(--bg-primary);
  position: relative;
  transition: background-color var(--transition-slow);
}

.layout.sidebar-collapsed .main {
  margin-left: 0;
}

/* Esconder sidebar quando colapsado no desktop */
.layout.sidebar-collapsed .sidebar {
  transform: translateX(-100%);
  position: fixed;
}

@media (max-width: 767px) {
  .layout.sidebar-collapsed .sidebar {
    transform: translateX(-100%);
  }
}

/* === MOBILE HEADER === */
.mobile-header {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: var(--sidebar-bg);
  z-index: var(--z-fixed);
  align-items: center;
  padding: 0 var(--space-4);
  gap: var(--space-4);
  box-shadow: var(--shadow-md);
}

@media (max-width: 767px) {
  .mobile-header {
    display: flex;
  }
}

.mobile-menu-btn {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-lg);
  transition: all var(--transition-base);
}

.mobile-menu-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.mobile-brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.mobile-brand .brand-icon {
  font-size: var(--font-xl);
}

.mobile-brand .brand-name {
  font-size: var(--font-lg);
  font-weight: 700;
  color: white;
}

/* === MOBILE OVERLAY === */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: calc(var(--z-fixed) + 1);
  opacity: 0;
  visibility: hidden;
  transition: all var(--transition-base);
}

.mobile-overlay.active {
  opacity: 1;
  visibility: visible;
}

@media (max-width: 767px) {
  .mobile-overlay {
    display: block;
  }
}

/* === SIDEBAR === */
.sidebar {
  width: var(--sidebar-width);
  background: var(--sidebar-bg);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  border-right: 1px solid var(--border-primary);
  transition: transform var(--transition-slow);
}

@media (max-width: 767px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: calc(var(--z-fixed) + 2);
    transform: translateX(-100%);
    /* Closed off-canvas: the 20px shadow still bled onto the page edge as a grey strip. */
    box-shadow: none;
  }

  .sidebar.mobile-open {
    transform: translateX(0);
    box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
  }
}

/* === LOGO WRAPPER === */
.logo-wrapper {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 64px;
  background: rgba(0, 0, 0, 0.15);
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-icon {
  font-size: 28px;
  line-height: 1;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.brand-name {
  font-size: 15px;
  font-weight: 700;
  color: white;
  letter-spacing: -0.3px;
  line-height: 1.2;
}

.brand-tagline {
  font-size: 10px;
  color: var(--primary-300);
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
}

/* === COLLAPSE/CLOSE BUTTONS === */
.collapse-btn,
.close-mobile-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-xs);
  transition: all var(--transition-base);
}

.collapse-btn:hover,
.close-mobile-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Hide/Show utilities */
@media (max-width: 767px) {
  .hide-mobile {
    display: none !important;
  }
}

@media (min-width: 768px) {
  .hide-desktop {
    display: none !important;
  }
}

/* === EXPAND BUTTON === */
.expand-btn {
  position: fixed;
  top: var(--space-4);
  left: var(--space-4);
  z-index: var(--z-fixed);
  width: 44px;
  height: 44px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-accent);
  background: var(--gradient-primary);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-lg);
  box-shadow: var(--shadow-glow-primary);
  transition: all var(--transition-base);
}

.expand-btn:hover {
  background: var(--gradient-primary-hover);
  transform: scale(1.05);
}

/* === MENU === */
.menu {
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex-grow: 1;
  overflow-y: auto;
  /* Rolagem "flutuando" sobre o gradiente: sem trilho, só a alça translúcida.
     O trilho global (claro) aparecia como uma faixa branca na lateral. */
  scrollbar-width: thin;
  scrollbar-color: var(--sidebar-scrollbar-thumb) transparent;
}

.menu::-webkit-scrollbar-track {
  background: transparent;
}

.menu::-webkit-scrollbar-thumb {
  background: var(--sidebar-scrollbar-thumb);
}

.menu::-webkit-scrollbar-thumb:hover {
  background: var(--sidebar-scrollbar-thumb-hover);
}

/* Menu de sistema (configurações) - não cresce, fica no final */
.menu-system {
  flex-grow: 0;
  padding-top: var(--space-3);
  padding-bottom: var(--space-3);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
}

.menu a {
  color: var(--sidebar-text);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--font-base);
  font-weight: 500;
  transition: all var(--transition-base);
  border: 1px solid transparent;
}

.menu a:hover {
  background: var(--sidebar-item-hover);
  color: var(--sidebar-text-active);
  border-color: rgba(255, 255, 255, 0.1);
}

.menu a.router-link-active {
  background: var(--sidebar-item-active);
  color: var(--sidebar-text-active);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

/* PRD-002 RF-17 — grupo expansível na sidebar */
.menu-group {
  display: flex;
  flex-direction: column;
}

.menu-group-toggle {
  background: transparent;
  border: 1px solid transparent;
  color: var(--sidebar-text);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-size: var(--font-base);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-base);
  text-align: left;
  width: 100%;
}

.menu-group-toggle:hover {
  background: var(--sidebar-item-hover);
  color: var(--sidebar-text-active);
  border-color: rgba(255, 255, 255, 0.1);
}

.menu-group-toggle.active {
  background: var(--sidebar-item-active);
  color: var(--sidebar-text-active);
  border-color: rgba(255, 255, 255, 0.2);
}

.menu-group-toggle .chevron {
  margin-left: auto;
  font-size: var(--font-sm);
  transition: transform var(--transition-base);
}

.menu-group.expanded .chevron {
  transform: rotate(180deg);
}

.menu-children {
  display: flex;
  flex-direction: column;
  margin-left: var(--space-6);
  margin-top: var(--space-1);
  gap: var(--space-1);
}

.menu-children a {
  font-size: var(--font-sm);
  padding: var(--space-2) var(--space-3);
}

.menu a i {
  font-size: var(--font-md);
  width: 20px;
  text-align: center;
}

.menu-label {
  flex: 1;
}

.menu-badge {
  background: var(--accent-danger, #e74c3c);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  padding: 0 5px;
  margin-left: 4px;
}

/* === USER INFO === */
.user-info {
  border-top: 1px solid var(--border-primary);
  padding: var(--space-4);
  font-size: var(--font-base);
  background: rgba(0, 0, 0, 0.2);
}

.user-profile-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  text-decoration: none;
  transition: all var(--transition-base);
  margin-bottom: var(--space-3);
}

.user-profile-link:hover {
  background: rgba(255, 255, 255, 0.1);
}

.user-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--level-color, #6366f1) 0%, color-mix(in srgb, var(--level-color, #6366f1) 70%, #000) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: var(--font-sm);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.user-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.level-indicator {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  border: 2px solid var(--bg-sidebar);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.name {
  font-weight: 600;
  margin: 0;
  color: white;
  font-size: var(--font-sm);
  line-height: 1.3;
}

.role {
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.95;
}

/* Tooltip customizado para o link do perfil */
.user-profile-link[title] {
  position: relative;
}

.user-profile-link[title]:hover::after {
  content: attr(title);
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: var(--bg-tertiary);
  color: var(--text-primary);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--font-xs);
  white-space: nowrap;
  z-index: var(--z-tooltip);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-primary);
  pointer-events: none;
  animation: tooltipFadeIn 0.2s ease;
}

.user-profile-link[title]:hover::before {
  content: '';
  position: absolute;
  left: 50%;
  bottom: calc(100% + 2px);
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: var(--bg-tertiary);
  z-index: var(--z-tooltip);
  pointer-events: none;
  animation: tooltipFadeIn 0.2s ease;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.logout-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--sidebar-text);
  font-size: var(--font-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  width: 100%;
  transition: all var(--transition-base);
}

.logout-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.4);
  color: var(--danger-text);
}

.version-info {
  text-align: center;
  font-size: 10px;
  color: var(--primary-400);
  margin-top: var(--space-3);
  padding-top: var(--space-2);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  letter-spacing: 0.5px;
}

/* === MAIN CONTENT === */
.main {
  flex: 1;
  padding: var(--space-6) var(--space-8);
  overflow-y: auto;
  background-color: var(--bg-primary);
  transition: all var(--transition-slow);
}

@media (max-width: 767px) {
  .main {
    padding: calc(60px + var(--space-4)) var(--space-4) var(--space-4);
  }
}

@media (max-width: 576px) {
  .main {
    padding: calc(60px + var(--space-3)) var(--space-3) var(--space-3);
  }
}

.sidebar-collapsed .main {
  padding: var(--space-6) var(--space-8) var(--space-6) 72px;
}

@media (max-width: 767px) {
  .sidebar-collapsed .main {
    padding: calc(60px + var(--space-4)) var(--space-4) var(--space-4);
  }
}

/* === LOADING === */
.loading-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: var(--space-4);
  font-size: var(--font-lg);
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  color: var(--text-muted);
  background-color: var(--bg-primary);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-secondary);
  border-top-color: var(--primary-500);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-hint {
  font-size: var(--font-sm);
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
}

.btn-text {
  background: none;
  border: none;
  color: var(--accent-primary, #6366f1);
  cursor: pointer;
  font-size: var(--font-sm);
  text-decoration: underline;
}

.btn-text:hover {
  color: var(--accent-primary-dark, #4f46e5);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* === ERROR LAYOUT - Elegant Design === */
.error-layout {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  min-height: 100vh;
  padding: var(--space-6);
  padding-top: var(--space-8);
  background: linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  overflow-y: auto;
}

.error-card {
  background: white;
  border-radius: 20px;
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 20px 50px -12px rgba(0, 0, 0, 0.15);
  max-width: 600px;
  width: 100%;
  overflow: hidden;
  margin-bottom: var(--space-8);
}

.error-card-header {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  padding: var(--space-6) var(--space-6);
  text-align: center;
  color: white;
}

.error-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-3);
  backdrop-filter: blur(10px);
}

.error-icon-wrapper i {
  font-size: 28px;
  color: white;
}

.error-card-header h2 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 var(--space-2);
  letter-spacing: -0.025em;
}

.error-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

.error-card-body {
  padding: var(--space-6);
}

.error-actions {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-6);
}

.btn-primary,
.btn-secondary {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.error-divider {
  display: flex;
  align-items: center;
  margin-bottom: var(--space-5);
}

.error-divider::before,
.error-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.error-divider span {
  padding: 0 var(--space-3);
  font-size: 12px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* User Info Card */
.user-info-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: var(--space-4);
  margin-bottom: var(--space-4);
}

.user-info-header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: var(--space-4);
}

.user-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-avatar span {
  color: white;
  font-weight: 700;
  font-size: 16px;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-details h4 {
  margin: 0 0 2px;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-details p {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-level-badge {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.user-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-3);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-value {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

.info-value.code {
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 11px;
  background: #e2e8f0;
  padding: 3px 6px;
  border-radius: 4px;
  word-break: break-all;
}

.info-value.error-id {
  background: #fef2f2;
  color: #dc2626;
}

/* Copy Report Button */
.btn-copy-report {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-4);
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  margin-bottom: var(--space-4);
}

.btn-copy-report:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
}

.btn-copy-report.copied {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-copy-report i {
  font-size: 18px;
}

/* Technical Details */
.technical-details {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.technical-details summary {
  padding: var(--space-3) var(--space-4);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  transition: all 0.2s;
  list-style: none;
}

.technical-details summary::-webkit-details-marker {
  display: none;
}

.technical-details summary:hover {
  background: #f1f5f9;
  color: #475569;
}

.technical-details summary i:first-child {
  color: #6366f1;
}

.technical-details summary span {
  flex: 1;
}

.technical-details summary .chevron {
  font-size: 12px;
  transition: transform 0.2s;
}

.technical-details[open] summary .chevron {
  transform: rotate(180deg);
}

.technical-content {
  border-top: 1px solid #e2e8f0;
  background: #1e293b;
  padding: var(--space-3);
  max-height: 350px;
  overflow: auto;
}

.technical-content pre {
  margin: 0;
  font-size: 10px;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  color: #94a3b8;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}

/* Scrollbar for technical content */
.technical-content::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.technical-content::-webkit-scrollbar-track {
  background: #334155;
  border-radius: 3px;
}

.technical-content::-webkit-scrollbar-thumb {
  background: #64748b;
  border-radius: 3px;
}

.technical-content::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Mobile Responsiveness */
@media (max-width: 576px) {
  .error-layout {
    padding: var(--space-2);
    padding-top: var(--space-3);
    align-items: flex-start;
  }
  
  .error-card {
    border-radius: 12px;
    margin-bottom: var(--space-4);
  }
  
  .error-card-header {
    padding: var(--space-4) var(--space-3);
  }
  
  .error-icon-wrapper {
    width: 48px;
    height: 48px;
  }
  
  .error-icon-wrapper i {
    font-size: 20px;
  }
  
  .error-card-header h2 {
    font-size: 16px;
  }
  
  .error-subtitle {
    font-size: 13px;
  }
  
  .error-card-body {
    padding: var(--space-3);
  }
  
  .error-actions {
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .btn-primary,
  .btn-secondary {
    padding: var(--space-3);
    font-size: 13px;
  }
  
  .error-divider {
    margin-bottom: var(--space-3);
  }
  
  .error-divider span {
    font-size: 11px;
  }
  
  .user-info-card {
    padding: var(--space-3);
    margin-bottom: var(--space-3);
  }
  
  .user-info-header {
    flex-wrap: wrap;
    gap: var(--space-2);
    padding-bottom: var(--space-3);
    margin-bottom: var(--space-3);
  }
  
  .user-avatar {
    width: 40px;
    height: 40px;
  }
  
  .user-avatar span {
    font-size: 14px;
  }
  
  .user-details h4 {
    font-size: 14px;
  }
  
  .user-details p {
    font-size: 12px;
  }
  
  .user-level-badge {
    margin-top: var(--space-1);
    order: 3;
    width: 100%;
    text-align: center;
    padding: 3px 8px;
    font-size: 10px;
  }
  
  .user-info-grid {
    grid-template-columns: 1fr;
    gap: var(--space-2);
  }
  
  .info-label {
    font-size: 10px;
  }
  
  .info-value {
    font-size: 12px;
  }
  
  .info-value.code {
    font-size: 10px;
    padding: 2px 4px;
  }
  
  .btn-copy-report {
    padding: var(--space-3);
    font-size: 13px;
    margin-bottom: var(--space-3);
  }
  
  .btn-copy-report i {
    font-size: 16px;
  }
  
  .technical-details summary {
    padding: var(--space-2) var(--space-3);
    font-size: 12px;
  }
  
  .technical-content {
    padding: var(--space-2);
    max-height: 250px;
  }
  
  .technical-content pre {
    font-size: 8px;
    line-height: 1.4;
  }
}

/* Tablet/Medium screens */
@media (min-width: 577px) and (max-width: 768px) {
  .error-card {
    max-width: 480px;
  }
  
  .technical-content pre {
    font-size: 9px;
  }
}

/* Larger screens */
@media (min-width: 769px) {
  .error-layout {
    padding-top: var(--space-6);
  }
  
  .error-card {
    max-width: 580px;
  }
  
  .technical-content {
    max-height: 400px;
  }
  
  .technical-content pre {
    font-size: 10px;
  }
}
</style>
